import { io, type Socket } from 'socket.io-client';
import { ApiError } from './api';
import { trackAnalytics } from './analytics';

export const SESSION_PROTOCOL_VERSION = 1;
const activeSessionKey = 'utage.activeGameSessionId';

export type ConnectionState = 'idle' | 'connecting' | 'restoring' | 'live' | 'offline';

export type PairChoice = 'a' | 'b' | 'c' | 'd';
export interface PairChoiceOption { key: PairChoice; label: string; imageUrl: string | null; imageAlt: string | null; fallbackImageUrl: string | null; }

export interface SessionSnapshot {
  protocolVersion: 1;
  session: { id: string; status: string; revision: number; momentKey: 'deep' | 'same_brain' | 'read_me' | 'dont_match'; interactionType: 'same_brain' | 'read_me' | 'dont_match'; contextType: 'friend' | 'stranger'; depthMode: 'normal' | 'deep'; expiresAt: string };
  players: Array<{ userId: string; firstName: string; photoUrl: string | null; hasCustomPhoto: boolean; customPhotoUpdatedAt: string | null; role: 'host' | 'player'; accepted: boolean; explicitlyLeft: boolean; presence: 'present' | 'reconnecting' | 'left' }>;
  customPrompt: null | { id: string; creatorUserId: string; saved: boolean };
  sameBrain: {
    totalRounds: number;
    currentRound: null | {
      id: string; number: number; phase: 'answering' | 'revealed'; question: string;
      options: PairChoiceOption[];
      myAnswer: { key: PairChoice; label: string } | null;
      opponentLocked: boolean;
      reveal: null | {
        answers: Array<{ userId: string; firstName: string; key: PairChoice; label: string }>;
        matched: boolean;
        reactions: Array<{ userId: string; firstName: string; reaction: '😂' | '😳' | '🔥' | '🤝' }>;
      };
    };
    score: { matches: number; completedRounds: number; percent: number };
    rematch: { requestedByMe: boolean; opponentRequested: boolean; myMode: 'normal' | 'deep' | null; opponentMode: 'normal' | 'deep' | null; newSessionId: string | null };
  };
  readMe: {
    totalRounds: number;
    currentRound: null | {
      id: string; number: number; phase: 'answering' | 'revealed'; role: 'target' | 'predictor';
      spotlight: { userId: string; firstName: string }; question: string;
      options: PairChoiceOption[];
      myAnswer: { key: PairChoice; label: string } | null;
      partnerLocked: boolean;
      reveal: null | {
        actual: { userId: string; firstName: string; key: PairChoice; label: string };
        prediction: { userId: string; firstName: string; key: PairChoice; label: string };
        correct: boolean;
        reactions: Array<{ userId: string; firstName: string; reaction: '😂' | '😳' | '🔥' | '🤝' }>;
      };
    };
    score: { correct: number; completedRounds: number; percent: number };
    rematch: { requestedByMe: boolean; opponentRequested: boolean; myMode: 'normal' | 'deep' | null; opponentMode: 'normal' | 'deep' | null; newSessionId: string | null };
  };
  dontMatch: {
    totalRounds: number;
    currentRound: null | {
      id: string; number: number; phase: 'answering' | 'revealed'; question: string;
      options: PairChoiceOption[];
      myAnswer: { key: PairChoice; label: string } | null;
      opponentLocked: boolean;
      reveal: null | {
        answers: Array<{ userId: string; firstName: string; key: PairChoice; label: string }>;
        survived: boolean;
        reactions: Array<{ userId: string; firstName: string; reaction: '😂' | '😳' | '🔥' | '🤝' }>;
      };
    };
    score: { survived: number; completedRounds: number; percent: number };
    rematch: { requestedByMe: boolean; opponentRequested: boolean; myMode: 'normal' | 'deep' | null; opponentMode: 'normal' | 'deep' | null; newSessionId: string | null };
  };
}

export const activeSessionId = (): string | null => localStorage.getItem(activeSessionKey);
export const rememberActiveSession = (id: string | null): void => {
  if (id) localStorage.setItem(activeSessionKey, id); else localStorage.removeItem(activeSessionKey);
};

export const fetchSessionSnapshot = async (sessionId: string, initData: string): Promise<SessionSnapshot> => {
  const response = await fetch(`/game-sessions/${encodeURIComponent(sessionId)}/state`, {
    cache: 'no-store',
    headers: { Accept: 'application/json', Authorization: `tma ${initData}`, 'X-UTAGE-Protocol': '1' }
  });
  const body = await response.json().catch(() => ({})) as SessionSnapshot & { error?: { code?: string; message?: string; requestId?: string } };
  if (!response.ok) {
    throw new ApiError(body.error?.message ?? 'Session restore failed.', body.error?.code ?? 'SESSION_RESTORE_FAILED', body.error?.requestId);
  }
  return body;
};

const postGameAction = async (
  sessionId: string,
  interaction: 'same-brain' | 'read-me' | 'dont-match',
  path: string,
  initData: string,
  body: Record<string, unknown>,
  actionId: string = crypto.randomUUID()
): Promise<SessionSnapshot> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 10_000);
  try {
    const response = await fetch(`/game-sessions/${encodeURIComponent(sessionId)}/${interaction}/${path}`, {
      method: 'POST', signal: controller.signal,
      headers: { Accept: 'application/json', Authorization: `tma ${initData}`, 'Content-Type': 'application/json', 'X-UTAGE-Protocol': '1' },
      body: JSON.stringify({ actionId, ...body })
    });
    const result = await response.json().catch(() => ({})) as {
      snapshot?: SessionSnapshot;
      error?: { code?: string; message?: string; requestId?: string };
    };
    if (!response.ok || !result.snapshot) {
      throw new ApiError(
        result.error?.message ?? 'Could not update the game.',
        result.error?.code ?? 'GAME_ACTION_FAILED',
        result.error?.requestId
      );
    }
    return result.snapshot;
  } catch (error) {
    if (controller.signal.aborted) {
      if (path === 'answer') trackAnalytics('answer_ack_timeout', { interactionType: interaction.replaceAll('-', '_') });
      throw new ApiError(
        path === 'answer' ? 'Could not confirm that answer yet.' : 'Could not confirm that action yet.',
        path === 'answer' ? 'ANSWER_ACK_TIMEOUT' : 'ACTION_ACK_TIMEOUT'
      );
    }
    throw error;
  } finally { window.clearTimeout(timeout); }
};

export const answerSameBrain = (sessionId: string, initData: string, choice: PairChoice, expectedRevision: number, actionId?: string) =>
  postGameAction(sessionId, 'same-brain', 'answer', initData, { choice, expectedRevision }, actionId);
export const reactSameBrain = (sessionId: string, initData: string, reaction: '😂' | '😳' | '🔥' | '🤝') =>
  postGameAction(sessionId, 'same-brain', 'reaction', initData, { reaction });
export const nextSameBrainRound = (sessionId: string, initData: string) => postGameAction(sessionId, 'same-brain', 'next', initData, {});
export const requestSameBrainRematch = (sessionId: string, initData: string, mode: 'normal' | 'deep' = 'normal') => postGameAction(sessionId, 'same-brain', 'rematch', initData, { mode });

export const answerReadMe = (sessionId: string, initData: string, choice: PairChoice, expectedRevision: number, actionId?: string) =>
  postGameAction(sessionId, 'read-me', 'answer', initData, { choice, expectedRevision }, actionId);
export const reactReadMe = (sessionId: string, initData: string, reaction: '😂' | '😳' | '🔥' | '🤝') =>
  postGameAction(sessionId, 'read-me', 'reaction', initData, { reaction });
export const nextReadMeRound = (sessionId: string, initData: string) => postGameAction(sessionId, 'read-me', 'next', initData, {});
export const requestReadMeRematch = (sessionId: string, initData: string, mode: 'normal' | 'deep' = 'normal') => postGameAction(sessionId, 'read-me', 'rematch', initData, { mode });

export const answerDontMatch = (sessionId: string, initData: string, choice: PairChoice, expectedRevision: number, actionId?: string) =>
  postGameAction(sessionId, 'dont-match', 'answer', initData, { choice, expectedRevision }, actionId);
export const reactDontMatch = (sessionId: string, initData: string, reaction: '😂' | '😳' | '🔥' | '🤝') =>
  postGameAction(sessionId, 'dont-match', 'reaction', initData, { reaction });
export const nextDontMatchRound = (sessionId: string, initData: string) => postGameAction(sessionId, 'dont-match', 'next', initData, {});
export const requestDontMatchRematch = (sessionId: string, initData: string, mode: 'normal' | 'deep' = 'normal') => postGameAction(sessionId, 'dont-match', 'rematch', initData, { mode });

export const endSession = async (sessionId: string, initData: string, actionId: string): Promise<SessionSnapshot> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 10_000);
  try {
    const response = await fetch(`/game-sessions/${encodeURIComponent(sessionId)}/actions`, {
      method: 'POST',
      signal: controller.signal,
      cache: 'no-store',
      headers: { Accept: 'application/json', Authorization: `tma ${initData}`, 'Content-Type': 'application/json', 'X-UTAGE-Protocol': '1' },
      body: JSON.stringify({ actionId, type: 'leave' })
    });
    const result = await response.json().catch(() => ({})) as {
      snapshot?: SessionSnapshot;
      error?: { code?: string; message?: string; requestId?: string };
    };
    if (!response.ok || !result.snapshot) {
      throw new ApiError(
        result.error?.message ?? 'Could not end this game yet.',
        result.error?.code ?? 'END_GAME_FAILED',
        result.error?.requestId
      );
    }
    return result.snapshot;
  } catch (error) {
    if (controller.signal.aborted) throw new ApiError('Could not confirm that the game ended yet.', 'END_GAME_ACK_TIMEOUT');
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
};

export const connectSessionCore = (options: {
  sessionId: string; initData: string; onState: (state: ConnectionState) => void;
  onSnapshot: (snapshot: SessionSnapshot, animate: boolean) => void;
}): (() => void) => {
  const sessionId = options.sessionId;
  let disposed = false;
  let socket: Socket | undefined;
  let disconnected = false;

  const replaceFromAuthority = async (reason: 'initial' | 'visibility' | 'online' | 'fallback'): Promise<void> => {
    if (reason !== 'initial') trackAnalytics('recovery_attempted', { scope: 'session', mechanism: 'snapshot' });
    options.onState('restoring');
    try {
      const snapshot = await fetchSessionSnapshot(sessionId, options.initData);
      if (!disposed) {
        options.onSnapshot(snapshot, false); options.onState('live');
        if (reason !== 'initial') {
          trackAnalytics('snapshot_recovery_used', { scope: 'session', mechanism: reason });
          trackAnalytics('recovery_succeeded', { scope: 'session', mechanism: 'snapshot' });
        }
      }
    } catch {
      if (reason !== 'initial') trackAnalytics('recovery_failed', { scope: 'session', mechanism: 'snapshot' });
      if (!disposed) options.onState(navigator.onLine ? 'connecting' : 'offline');
    }
  };

  options.onState('connecting');
  socket = io({ auth: { initData: options.initData, protocolVersion: SESSION_PROTOCOL_VERSION }, reconnection: true });
  socket.on('connect', () => {
    if (disconnected) trackAnalytics('recovery_attempted', { scope: 'session', mechanism: 'socket_resume' });
    socket?.emit('session:resume', { sessionId }, (result: { ok: boolean; snapshot?: SessionSnapshot }) => {
      if (result.ok && result.snapshot && !disposed) {
        options.onSnapshot(result.snapshot, false);
        options.onState('live');
        if (disconnected) trackAnalytics('recovery_succeeded', { scope: 'session', mechanism: 'socket_resume' });
        disconnected = false;
      } else void replaceFromAuthority('fallback');
    });
  });
  socket.on('disconnect', () => { if (!disposed) { disconnected = true; trackAnalytics('socket_disconnected', { scope: 'session' }); options.onState(navigator.onLine ? 'connecting' : 'offline'); } });
  socket.on('session:changed', () => {
    void fetchSessionSnapshot(sessionId, options.initData).then((snapshot) => {
      if (!disposed) options.onSnapshot(snapshot, true);
    }).catch(() => undefined);
  });
  const onVisible = (): void => { if (document.visibilityState === 'visible') void replaceFromAuthority('visibility'); };
  const onOnline = (): void => void replaceFromAuthority('online');
  const onOffline = (): void => options.onState('offline');
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  void replaceFromAuthority('initial');
  return () => {
    disposed = true; socket?.disconnect();
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};
